import { useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent, MouseEvent } from 'react';
import { useForm } from 'react-hook-form';

import Button from '@/components/Button';
import { FormLayout, Larger } from '@/components/Forms';
import Checkbox from '@/components/Forms/Checkbox';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import useCompaniesOptions from '@/hooks/useCompaniesOptions';
import { FreelancerSubunitResource } from '@/types/resources';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

type FormData = {
  name: string;
  company_id: number | null;
  hide_from_dropdowns: boolean;
};

interface Props {
  subunit?: FreelancerSubunitResource;
  onClose: () => void;
}

const FreelancerSubunitFormModal: FC<Props> = ({ subunit, onClose }) => {
  const queryClient = useQueryClient();
  const alerts = useAlerts();

  const { data: companyOptions = [] } = useCompaniesOptions();

  const visibleCompanyOptions = companyOptions.filter(
    (company) => !company.hide_from_dropdowns,
  );

  const {
    handleSubmit,
    setError,
    clearErrors,
    control,
    formState: { isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      name: subunit?.name || '',
      company_id: subunit?.company_id ?? null,
      hide_from_dropdowns: subunit?.hide_from_dropdowns || false,
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (formData) => {
      // Subunits predate the company link, so every save now has to set one.
      if (!formData.company_id) {
        setError('company_id', { message: 'Company is required' });
        return;
      }

      try {
        const data = {
          name: formData.name,
          company_id: formData.company_id,
          hide_from_dropdowns: formData.hide_from_dropdowns,
        };

        if (subunit) {
          await api.put<typeof data, FreelancerSubunitResource>(
            `/v1/freelancer-subunits/${subunit.id}`,
            data,
          );
        } else {
          await api.post<typeof data, FreelancerSubunitResource>(
            '/v1/freelancer-subunits',
            data,
          );
        }

        await queryClient.invalidateQueries(['freelancer_subunits'], {
          exact: false,
        });

        onClose();
        alerts.success(
          `Subunit was successfully ${subunit ? 'updated' : 'created'}`,
        );
      } catch (error) {
        handleFormErrors<FormData>(error, setError, alerts);
      }
    })(e);
  };

  const onCancel = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClose();
  };

  return (
    <Modal>
      <SidePanel
        heading={`${subunit ? 'Edit' : 'Add'} Subunit`}
        onClose={onClose}
      >
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FormField<FormData>
                control={control}
                name="name"
                label="Name"
                id="nameInput"
                placeholder="Add subunit name"
              />
              <FormField<FormData, number>
                control={control}
                name="company_id"
                label="Company"
                id="companySelect"
                type="select"
                options={visibleCompanyOptions}
              />
              <Larger>
                <Checkbox control={control} name="hide_from_dropdowns">
                  Hide from dropdowns
                </Checkbox>
              </Larger>
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              {subunit ? 'Update' : 'Add'}
            </Button>
            <Button color="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default FreelancerSubunitFormModal;
